/**
 * The two statements the customer must accept. Both are required — the server refuses otherwise.
 * The wording comes from the server, pinned to the version in force when the link was sent.
 */

import { Checkbox } from '@design-system';
import { NYL_AGENT_ENTERED_DATA_USE_TEXT, NYL_AGENT_ENTERED_PERMISSION_TEXT } from '@shared';

const PRIVACY_POLICY_URL = 'https://www.waterlily.com/privacy-policy';
const TERMS_OF_SERVICE_URL = 'https://www.waterlily.com/terms-of-service';

interface AgentEnteredConsentStepProps {
  permissionText: string;
  acknowledgementText: string;
  acceptedPermission: boolean;
  acceptedAcknowledgement: boolean;
  disabled?: boolean;
  onAcceptPermission: (accepted: boolean) => void;
  onAcceptAcknowledgement: (accepted: boolean) => void;
}

interface ConsentCheckboxProps {
  id: string;
  text: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}

function ApprovedConsentText({ text }: { text: string }) {
  if (text === NYL_AGENT_ENTERED_PERMISSION_TEXT) {
    const separatorIndex = text.indexOf(':');

    return (
      <>
        <strong>{text.slice(0, separatorIndex)}</strong>
        {text.slice(separatorIndex)}
      </>
    );
  }

  if (text === NYL_AGENT_ENTERED_DATA_USE_TEXT) {
    const labelEndIndex = text.indexOf(':') + 1;
    const privacyLabel = 'Privacy Policy';
    const termsLabel = 'Terms of Service';
    const privacyIndex = text.indexOf(privacyLabel);
    const termsIndex = text.indexOf(termsLabel);

    return (
      <>
        <strong>{text.slice(0, labelEndIndex)}</strong>
        {text.slice(labelEndIndex, privacyIndex)}
        <a
          href={PRIVACY_POLICY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline"
        >
          {privacyLabel}
        </a>
        {text.slice(privacyIndex + privacyLabel.length, termsIndex)}
        <a
          href={TERMS_OF_SERVICE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline"
        >
          {termsLabel}
        </a>
        {text.slice(termsIndex + termsLabel.length)}
      </>
    );
  }

  return text;
}

function ConsentCheckbox({ id, text, checked, disabled, onChange }: ConsentCheckboxProps) {
  return (
    <div className="flex items-start gap-3">
      <Checkbox
        id={id}
        className="mt-0.5"
        disabled={disabled}
        checked={checked}
        onCheckedChange={next => onChange(next === true)}
      />
      <label
        htmlFor={id}
        className="text-foreground cursor-pointer text-sm leading-relaxed"
      >
        <ApprovedConsentText text={text} />
      </label>
    </div>
  );
}

export function AgentEnteredConsentStep({
  permissionText,
  acknowledgementText,
  acceptedPermission,
  acceptedAcknowledgement,
  disabled,
  onAcceptPermission,
  onAcceptAcknowledgement,
}: AgentEnteredConsentStepProps) {
  return (
    <div className="space-y-4">
      <ConsentCheckbox
        id="agent-entered-consent-permission"
        text={permissionText}
        checked={acceptedPermission}
        disabled={disabled}
        onChange={onAcceptPermission}
      />
      <ConsentCheckbox
        id="agent-entered-consent-acknowledgement"
        text={acknowledgementText}
        checked={acceptedAcknowledgement}
        disabled={disabled}
        onChange={onAcceptAcknowledgement}
      />
    </div>
  );
}
